import React, { useState, useEffect, useRef } from 'react';
import { BiArrowBack, BiCheckCircle, BiRefresh } from 'react-icons/bi';
import { BsSend, BsReply, BsFileEarmarkPdf, BsPaperclip, BsBoxArrowUpRight } from 'react-icons/bs';

import axios from '../../../axios';
import * as url from '../../../utils/url';
import { formatMessageTimestamp } from '../../../utils/stringUtils';
import { toastContainer } from '../../../utils/toast';
import { getOrgPrefix, withHashtagPrefix } from '../../../utils/hashtagPrefix';
import HashtagInsightsCard from './HashtagInsightsCard';
import HashtagDownloadButton from './HashtagDownloadButton';
import HashtagPdfDocument from './HashtagPdfDocument';
import {
    buildPdfFilename,
    exportNodesToPdf,
    formatPdfTimestamp,
    splitIntoPdfChunks,
    waitForNextFrame,
} from '../../../utils/hashtagPdfExport';
import {
    buildHashtagPdfAttachmentLine,
    buildHashtagPdfMessageCount,
    HASHTAG_CHAT_PDF_FAILED,
    HASHTAG_PDF_ATTACHMENT_LABEL,
    HASHTAG_PDF_CHAT_LABEL,
} from '../../../utils/triviaResultsStrings';

// ---------------------------------------------------------------------------
// Phone / mobile number helpers
// ---------------------------------------------------------------------------

/**
 * Extracts an E.164 mobile number from text if pattern @+<digits> is found.
 * Per API spec: backend also parses @<mobileNumber> from body — FE still sends
 * targetMobileNumber explicitly for safety.
 */
const extractMobileNumber = (text) => {
    var match = text.match(/@(\+?\d{7,15})/);
    if (!match) return null;
    var num = match[1];
    // Normalize to E.164
    if (num.charAt(0) === '+') {
        return num;
    }
    // If 11 digits starting with 1, just prepend +
    if (num.length === 11 && num.charAt(0) === '1') {
        return '+' + num;
    }
    // If 10 digits (no country code), prepend +1
    if (num.length === 10) {
        return '+1' + num;
    }
    // Fallback: prepend +
    return '+' + num;
};

/**
 * Returns the senderPhone of the last PARTICIPANT message in the feed.
 * Fallback when no @+phone is in the composer text and no replyContext is set.
 */
const getLastParticipantMobile = (msgs) => {
    for (var i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].senderType === 'PARTICIPANT' && msgs[i].senderPhone) {
            return msgs[i].senderPhone;
        }
    }
    return null;
};

/**
 * Resolves targetMobileNumber for the POST /reply payload.
 * Priority: (1) @+phone explicit in text → (2) replyContext phone → (3) last participant phone
 */
const resolveTargetMobile = (text, msgs, replyCtx) => {
    var fromText = extractMobileNumber(text);
    if (fromText) return fromText;
    if (replyCtx && replyCtx.senderPhone) return replyCtx.senderPhone;
    return getLastParticipantMobile(msgs);
};

/**
 * Best-effort phone map: scans ADMIN reply messages for @+phone targeting a userId.
 *
 * The GET /messages API does not return senderMobileNumber on participant messages.
 * TODO: request backend to add senderMobileNumber to the message object.
 *
 * Workaround: when an admin replied "@+13105551234 ..." with targetUserId "65aabc...",
 * we infer that userId "65aabc..." owns that phone number and cache it.
 */
var buildPhoneMap = function(msgs) {
    var map = {};
    msgs.forEach(function(m) {
        if (m.senderType === 'ADMIN' && m.targetUserId && m.body) {
            var phoneMatch = m.body.match(/@(\+\d{7,15})/);
            if (phoneMatch) {
                map[m.targetUserId] = phoneMatch[1];
            }
        }
    });
    return map;
};

/**
 * Applies the phone map to fill senderPhone on participant messages where possible.
 */
var applyPhoneMap = function(msgs) {
    var phoneMap = buildPhoneMap(msgs);
    if (Object.keys(phoneMap).length === 0) return msgs;
    return msgs.map(function(m) {
        if (m.senderType === 'PARTICIPANT' && !m.senderPhone && phoneMap[m.userId]) {
            return Object.assign({}, m, { senderPhone: phoneMap[m.userId] });
        }
        return m;
    });
};

/**
 * Returns initials from a display name string.
 */
const getInitials = (name) => {
    if (!name || !name.trim()) return '—';
    var parts = name.trim().split(' ').filter(function(n) { return n.length > 0; });
    if (parts.length === 0) return '—';
    return parts.map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
};


// ---------------------------------------------------------------------------
// Attachment helpers — used by hashtag drilldown message bubbles
// ---------------------------------------------------------------------------

const parseFileNameFromContentDisposition = function(contentDisposition) {
    if (!contentDisposition || typeof contentDisposition !== 'string') return '';

    try {
        var utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (utf8Match && utf8Match[1]) {
            return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
        }

        var plainMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
        if (plainMatch && plainMatch[1]) {
            return plainMatch[1].trim();
        }

        var unquotedMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
        if (unquotedMatch && unquotedMatch[1]) {
            return unquotedMatch[1].trim().replace(/^"|"$/g, '');
        }
    } catch (err) {
        return '';
    }

    return '';
};

const getFileNameFromUrl = function(fileUrl) {
    if (!fileUrl || typeof fileUrl !== 'string') return '';

    try {
        var parsedUrl = new URL(fileUrl, window.location.origin);

        // S3 signed URLs often contain:
        // response-content-disposition=inline; filename="IMG_4101.jpg"; filename*=UTF-8''IMG_4101.jpg
        // Prefer that filename if present because it is the user-facing filename.
        var disposition =
            parsedUrl.searchParams.get('response-content-disposition') ||
            parsedUrl.searchParams.get('ResponseContentDisposition') ||
            '';
        var dispositionFileName = parseFileNameFromContentDisposition(disposition);
        if (dispositionFileName) return dispositionFileName;

        var cleanPath = parsedUrl.pathname || '';
        var parts = cleanPath.split('/');
        return decodeURIComponent(parts[parts.length - 1] || '');
    } catch (err) {
        try {
            var cleanUrl = fileUrl.split('?')[0].split('#')[0];
            var pathParts = cleanUrl.split('/');
            return decodeURIComponent(pathParts[pathParts.length - 1] || '');
        } catch (fallbackErr) {
            return '';
        }
    }
};

const getFileExtension = function(fileNameOrUrl) {
    var rawValue = fileNameOrUrl ? String(fileNameOrUrl) : '';
    var value = rawValue.indexOf('?') !== -1 || rawValue.indexOf('/') !== -1
        ? (getFileNameFromUrl(rawValue) || rawValue)
        : rawValue;
    value = value.split('?')[0].split('#')[0];
    var parts = value.split('.');
    if (parts.length < 2) return '';
    return parts[parts.length - 1].toLowerCase();
};

const formatFileSize = function(size) {
    var bytes = Number(size);
    if (!bytes || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'];
const PDF_EXTENSIONS = ['pdf'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'amr', 'awb', 'flac', 'webm'];
const BROWSER_PLAYABLE_AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'flac', 'webm'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'wmv', 'mkv', '3gp', '3g2'];
const BROWSER_PLAYABLE_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'webm', 'mov'];

const getAttachmentExtension = function(attachment) {
    if (!attachment) return '';
    return (
        getFileExtension(attachment.fileName) ||
        getFileExtension(attachment.filename) ||
        getFileExtension(attachment.name) ||
        getFileExtension(attachment.originalName) ||
        getFileExtension(attachment.originalFilename) ||
        getFileExtension(attachment.url) ||
        getFileExtension(attachment.s3Url) ||
        getFileExtension(attachment.s3SignedUrl) ||
        getFileExtension(attachment.signedS3Url) ||
        getFileExtension(attachment.fileUrl) ||
        getFileExtension(attachment.mediaUrl) ||
        getFileExtension(attachment.attachmentUrl) ||
        getFileExtension(attachment.signedUrl) ||
        getFileExtension(attachment.secureUrl) ||
        getFileExtension(attachment.location) ||
        getFileExtension(attachment.href) ||
        getFileExtension(attachment.playableUrl) ||
        getFileExtension(attachment.browserPlayableUrl) ||
        getFileExtension(attachment.convertedUrl) ||
        ''
    );
};

const inferAttachmentKind = function(attachment) {
    if (!attachment) return 'file';

    if (attachment.kind && ['image', 'pdf', 'audio', 'video', 'file'].indexOf(String(attachment.kind).toLowerCase()) !== -1) {
        return String(attachment.kind).toLowerCase();
    }

    if (attachment.type && ['image', 'pdf', 'audio', 'video', 'file'].indexOf(String(attachment.type).toLowerCase()) !== -1) {
        return String(attachment.type).toLowerCase();
    }

    var mimeType =
        attachment.mimeType ||
        attachment.contentType ||
        attachment.mediaContentType ||
        attachment.playbackContentType ||
        '';
    mimeType = String(mimeType || '').toLowerCase();

    var extension = getAttachmentExtension(attachment);

    if (mimeType.indexOf('image/') === 0 || IMAGE_EXTENSIONS.indexOf(extension) !== -1) {
        return 'image';
    }

    if (mimeType.indexOf('pdf') !== -1 || PDF_EXTENSIONS.indexOf(extension) !== -1) {
        return 'pdf';
    }

    if (mimeType.indexOf('audio/') === 0 || AUDIO_EXTENSIONS.indexOf(extension) !== -1) {
        return 'audio';
    }

    if (mimeType.indexOf('video/') === 0 || VIDEO_EXTENSIONS.indexOf(extension) !== -1) {
        return 'video';
    }

    return 'file';
};

const isBrowserPlayableAudio = function(attachment) {
    if (!attachment) return false;

    var playbackMime =
        attachment.playbackContentType ||
        attachment.browserPlayableContentType ||
        attachment.convertedContentType ||
        attachment.mimeType ||
        '';
    playbackMime = String(playbackMime || '').toLowerCase();

    var playableUrl =
        attachment.browserPlayableUrl ||
        attachment.playableUrl ||
        attachment.convertedUrl ||
        attachment.mp3Url ||
        attachment.m4aUrl ||
        attachment.audioUrl ||
        attachment.url ||
        '';

    var extension = getFileExtension(playableUrl) || getAttachmentExtension(attachment);

    if (extension === 'amr') return false;
    if (playbackMime.indexOf('audio/amr') === 0) return false;

    return (
        playbackMime.indexOf('audio/mpeg') === 0 ||
        playbackMime.indexOf('audio/mp3') === 0 ||
        playbackMime.indexOf('audio/mp4') === 0 ||
        playbackMime.indexOf('audio/aac') === 0 ||
        playbackMime.indexOf('audio/wav') === 0 ||
        playbackMime.indexOf('audio/x-wav') === 0 ||
        playbackMime.indexOf('audio/ogg') === 0 ||
        playbackMime.indexOf('audio/webm') === 0 ||
        BROWSER_PLAYABLE_AUDIO_EXTENSIONS.indexOf(extension) !== -1
    );
};

const isBrowserPlayableVideo = function(attachment) {
    if (!attachment) return false;

    var playbackMime =
        attachment.playbackContentType ||
        attachment.browserPlayableContentType ||
        attachment.convertedContentType ||
        attachment.mimeType ||
        '';
    playbackMime = String(playbackMime || '').toLowerCase();

    var playableUrl =
        attachment.browserPlayableUrl ||
        attachment.playableUrl ||
        attachment.convertedUrl ||
        attachment.mp4Url ||
        attachment.videoUrl ||
        attachment.url ||
        '';

    var extension = getFileExtension(playableUrl) || getAttachmentExtension(attachment);

    return (
        playbackMime.indexOf('video/mp4') === 0 ||
        playbackMime.indexOf('video/webm') === 0 ||
        playbackMime.indexOf('video/quicktime') === 0 ||
        BROWSER_PLAYABLE_VIDEO_EXTENSIONS.indexOf(extension) !== -1
    );
};

const normalizeAttachment = function(rawAttachment) {
    if (!rawAttachment) return null;

    if (typeof rawAttachment === 'string') {
        var stringUrl = rawAttachment;
        var stringAttachment = {
            url: stringUrl,
            fileName: getFileNameFromUrl(stringUrl),
            mimeType: '',
            fileSize: null,
        };
        return Object.assign({}, stringAttachment, { kind: inferAttachmentKind(stringAttachment) });
    }

    if (typeof rawAttachment !== 'object') return null;

    var urlValue =
        rawAttachment.url ||
        rawAttachment.signedUrl ||
        rawAttachment.s3Url ||
        rawAttachment.s3SignedUrl ||
        rawAttachment.signedS3Url ||
        rawAttachment.fileUrl ||
        rawAttachment.mediaUrl ||
        rawAttachment.attachmentUrl ||
        rawAttachment.secureUrl ||
        rawAttachment.location ||
        rawAttachment.href ||
        '';

    var browserPlayableUrl =
        rawAttachment.browserPlayableUrl ||
        rawAttachment.playableUrl ||
        rawAttachment.playbackUrl ||
        rawAttachment.playbackMediaUrl ||
        rawAttachment.transcodedUrl ||
        rawAttachment.convertedUrl ||
        rawAttachment.previewUrl ||
        rawAttachment.audioPreviewUrl ||
        rawAttachment.videoPreviewUrl ||
        rawAttachment.streamUrl ||
        rawAttachment.streamingUrl ||
        rawAttachment.mp3Url ||
        rawAttachment.m4aUrl ||
        rawAttachment.mp4Url ||
        rawAttachment.audioUrl ||
        rawAttachment.videoUrl ||
        rawAttachment.browserPlayableS3Url ||
        rawAttachment.s3PlayableUrl ||
        '';

    var fileNameValue =
        rawAttachment.fileName ||
        rawAttachment.filename ||
        rawAttachment.name ||
        rawAttachment.originalName ||
        rawAttachment.originalFilename ||
        rawAttachment.key ||
        getFileNameFromUrl(urlValue) ||
        getFileNameFromUrl(browserPlayableUrl) ||
        'Attachment';

    var normalizedObjectAttachment = {
        url: urlValue || browserPlayableUrl,
        originalUrl: rawAttachment.originalUrl || urlValue || rawAttachment.s3Url || '',
        s3Url: rawAttachment.s3Url || '',
        s3SignedUrl: rawAttachment.s3SignedUrl || '',
        signedS3Url: rawAttachment.signedS3Url || '',
        browserPlayableUrl: browserPlayableUrl,
        playableUrl: rawAttachment.playableUrl || browserPlayableUrl,
        convertedUrl: rawAttachment.convertedUrl || rawAttachment.transcodedUrl || '',
        fileName: fileNameValue,
        mimeType:
            rawAttachment.mimeType ||
            rawAttachment.contentType ||
            rawAttachment.mediaContentType ||
            '',
        playbackContentType:
            rawAttachment.playbackContentType ||
            rawAttachment.browserPlayableContentType ||
            rawAttachment.convertedContentType ||
            rawAttachment.transcodedContentType ||
            '',
        fileSize:
            rawAttachment.fileSize ||
            rawAttachment.size ||
            rawAttachment.sizeBytes ||
            rawAttachment.bytes ||
            rawAttachment.contentLength ||
            null,
        kind: rawAttachment.kind || rawAttachment.type || '',
        type: rawAttachment.type || '',
    };

    return Object.assign({}, normalizedObjectAttachment, {
        kind: inferAttachmentKind(normalizedObjectAttachment),
    });
};

const appendAttachmentCandidates = function(candidates, value) {
    if (!value) return;
    if (Array.isArray(value)) {
        value.forEach(function(item) { appendAttachmentCandidates(candidates, item); });
        return;
    }
    candidates.push(value);
};

const getFirstMessagingAttachment = function(source) {
    if (!source) return null;

    var messagingResponse = source.messagingResponse || {};
    var candidates = [];

    appendAttachmentCandidates(candidates, source.attachment);
    appendAttachmentCandidates(candidates, source.attachments);
    appendAttachmentCandidates(candidates, source.media);
    appendAttachmentCandidates(candidates, source.mediaFile);
    appendAttachmentCandidates(candidates, source.mediaFiles);
    appendAttachmentCandidates(candidates, source.files);
    appendAttachmentCandidates(candidates, source.file);
    appendAttachmentCandidates(candidates, source.mediaUrl);
    appendAttachmentCandidates(candidates, source.mediaUrls);
    appendAttachmentCandidates(candidates, source.fileUrl);
    appendAttachmentCandidates(candidates, source.attachmentUrl);
    appendAttachmentCandidates(candidates, source.signedUrl);
    appendAttachmentCandidates(candidates, source.s3Url);
    appendAttachmentCandidates(candidates, source.s3SignedUrl);
    appendAttachmentCandidates(candidates, source.signedS3Url);

    appendAttachmentCandidates(candidates, messagingResponse.attachment);
    appendAttachmentCandidates(candidates, messagingResponse.attachments);
    appendAttachmentCandidates(candidates, messagingResponse.media);
    appendAttachmentCandidates(candidates, messagingResponse.mediaFile);
    appendAttachmentCandidates(candidates, messagingResponse.mediaFiles);
    appendAttachmentCandidates(candidates, messagingResponse.files);
    appendAttachmentCandidates(candidates, messagingResponse.file);
    appendAttachmentCandidates(candidates, messagingResponse.mediaUrl);
    appendAttachmentCandidates(candidates, messagingResponse.mediaUrls);
    appendAttachmentCandidates(candidates, messagingResponse.fileUrl);
    appendAttachmentCandidates(candidates, messagingResponse.attachmentUrl);
    appendAttachmentCandidates(candidates, messagingResponse.signedUrl);
    appendAttachmentCandidates(candidates, messagingResponse.s3Url);
    appendAttachmentCandidates(candidates, messagingResponse.s3SignedUrl);
    appendAttachmentCandidates(candidates, messagingResponse.signedS3Url);

    for (var i = 0; i < candidates.length; i++) {
        var normalized = normalizeAttachment(candidates[i]);
        if (normalized && (normalized.url || normalized.fileName)) {
            return normalized;
        }
    }

    return null;
};

const renderFileAttachmentCard = function(attachment, isMe, labelOverride) {
    var sizeText = formatFileSize(attachment.fileSize);
    var isPdf = attachment.kind === 'pdf';
    var isImage = attachment.kind === 'image';
    var typeLabel = labelOverride || (isPdf ? 'PDF attachment' : isImage ? 'Image attachment' : 'File attachment');

    var linkUrl = attachment.url || attachment.originalUrl || attachment.browserPlayableUrl || '';
    var linkProps = linkUrl
        ? { href: linkUrl, target: '_blank', rel: 'noopener noreferrer' }
        : { href: '#', onClick: function(event) { event.preventDefault(); } };

    return (
        <a
            {...linkProps}
            className={'uc-message-file-attachment ' + (isMe ? 'admin-file' : 'user-file')}
            title={attachment.fileName || typeLabel}
        >
            {isPdf ? <BsFileEarmarkPdf className="uc-message-file-icon pdf" /> : <BsPaperclip className="uc-message-file-icon" />}
            <span className="uc-message-file-copy">
                <span className="uc-message-file-name">{attachment.fileName || typeLabel}</span>
                {sizeText ? <span className="uc-message-file-meta">{sizeText}</span> : null}
            </span>
            {linkUrl ? <BsBoxArrowUpRight size={12} /> : null}
        </a>
    );
};

const renderMessageAttachment = function(rawAttachment, isMe) {
    var attachment = normalizeAttachment(rawAttachment);
    if (!attachment) return null;

    var linkUrl = attachment.url || attachment.originalUrl || attachment.browserPlayableUrl || '';
    var linkProps = linkUrl
        ? { href: linkUrl, target: '_blank', rel: 'noopener noreferrer' }
        : { href: '#', onClick: function(event) { event.preventDefault(); } };

    if (attachment.kind === 'image' && linkUrl) {
        return (
            <a
                {...linkProps}
                className="uc-message-image-link"
                title={attachment.fileName || 'View image attachment'}
            >
                <span className="uc-message-image-preview">
                    <img
                        src={linkUrl}
                        alt={attachment.fileName || 'Image attachment'}
                        className="uc-message-image-attachment"
                        onError={function(event) {
                            event.currentTarget.style.display = 'none';
                            var parent = event.currentTarget.parentNode;
                            var fallback = parent && parent.querySelector ? parent.querySelector('.uc-message-image-fallback') : null;
                            if (fallback) {
                                fallback.style.display = 'flex';
                            }
                        }}
                    />
                    <span className="uc-message-image-fallback" style={{ display: 'none' }}>
                        <BsPaperclip className="uc-message-file-icon" />
                        <span className="uc-message-file-copy">
                            <span className="uc-message-file-name">{attachment.fileName || 'Image attachment'}</span>
                            <span className="uc-message-file-meta">Open image</span>
                        </span>
                    </span>
                </span>
                <span className="uc-message-image-caption">
                    Open image <BsBoxArrowUpRight size={12} />
                </span>
            </a>
        );
    }

    if (attachment.kind === 'image') {
        return renderFileAttachmentCard(attachment, isMe, 'Image attachment');
    }

    var sizeText = formatFileSize(attachment.fileSize);

    if (attachment.kind === 'audio') {
        var audioPlaybackUrl =
            attachment.browserPlayableUrl ||
            attachment.playableUrl ||
            attachment.convertedUrl ||
            attachment.mp3Url ||
            attachment.m4aUrl ||
            attachment.audioUrl ||
            attachment.url ||
            '';

        var canPlayAudio = isBrowserPlayableAudio(Object.assign({}, attachment, { url: audioPlaybackUrl }));

        return (
            <div className={'uc-message-media-attachment audio ' + (isMe ? 'admin-media' : 'user-media')}>
                <div className="uc-message-media-header">
                    <span className="uc-message-media-title">{attachment.fileName || 'Audio attachment'}</span>
                    {sizeText ? <span className="uc-message-media-meta">{sizeText}</span> : null}
                </div>
                {canPlayAudio ? (
                    <audio
                        controls
                        preload="metadata"
                        src={audioPlaybackUrl}
                        className="uc-message-audio-player"
                    >
                        Your browser does not support the audio element.
                    </audio>
                ) : (
                    <div className="uc-message-media-unavailable">
                        Audio preview unavailable for this format. Open or download the file.
                    </div>
                )}
                {(attachment.url || audioPlaybackUrl) ? (
                    <a
                        href={attachment.url || audioPlaybackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="uc-message-media-open-link"
                    >
                        {canPlayAudio ? 'Open audio' : 'Open/download audio'} <BsBoxArrowUpRight size={12} />
                    </a>
                ) : null}
            </div>
        );
    }

    if (attachment.kind === 'video') {
        var videoPlaybackUrl =
            attachment.browserPlayableUrl ||
            attachment.playableUrl ||
            attachment.convertedUrl ||
            attachment.mp4Url ||
            attachment.videoUrl ||
            attachment.url ||
            '';

        var canPlayVideo = isBrowserPlayableVideo(Object.assign({}, attachment, { url: videoPlaybackUrl }));

        return (
            <div className={'uc-message-media-attachment video ' + (isMe ? 'admin-media' : 'user-media')}>
                {canPlayVideo ? (
                    <video
                        controls
                        preload="metadata"
                        src={videoPlaybackUrl}
                        className="uc-message-video-player"
                    >
                        Your browser does not support the video element.
                    </video>
                ) : (
                    <div className="uc-message-media-unavailable">
                        Video preview unavailable for this format. Open or download the file.
                    </div>
                )}
                <div className="uc-message-media-footer">
                    <span className="uc-message-media-copy">
                        <span className="uc-message-media-title">{attachment.fileName || 'Video attachment'}</span>
                        {sizeText ? <span className="uc-message-media-meta">{sizeText}</span> : null}
                    </span>
                    {(attachment.url || videoPlaybackUrl) ? (
                        <a
                            href={attachment.url || videoPlaybackUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="uc-message-media-open-link"
                        >
                            Open <BsBoxArrowUpRight size={12} />
                        </a>
                    ) : null}
                </div>
            </div>
        );
    }

    return renderFileAttachmentCard(attachment, isMe);
};

const extractSavedMessageFromSendResponse = function(data) {
    if (!data) return null;
    if (data.message) return data.message;
    if (data.reply) return data.reply;
    if (data.savedMessage) return data.savedMessage;
    if (data.conversationMessage) return data.conversationMessage;
    if (data.data && data.data.message) return data.data.message;
    if (data.data && data.data.reply) return data.data.reply;
    if (Array.isArray(data.messages) && data.messages.length > 0) {
        return data.messages[data.messages.length - 1];
    }
    return null;
};

const sortMessagesAsc = function(msgs) {
    return (msgs || []).slice().sort(function(a, b) {
        return new Date(a.createdAt) - new Date(b.createdAt);
    });
};

const isLocalMessage = function(message) {
    return !!(message && message.id && String(message.id).indexOf('local-') === 0);
};

const hasEquivalentBackendMessage = function(localMessage, fetchedMessages) {
    if (!localMessage || !fetchedMessages || fetchedMessages.length === 0) return false;
    var localBody = (localMessage.body || '').trim();
    for (var i = 0; i < fetchedMessages.length; i++) {
        var fetched = fetchedMessages[i];
        var fetchedBody = (fetched.body || '').trim();
        if (
            !isLocalMessage(fetched) &&
            fetched.senderType === localMessage.senderType &&
            fetchedBody === localBody &&
            (!localMessage.targetUserId || !fetched.targetUserId || String(localMessage.targetUserId) === String(fetched.targetUserId))
        ) {
            return true;
        }
    }
    return false;
};

const mergeChatMessages = function(messages) {
    var seen = {};
    var merged = [];

    (messages || []).forEach(function(message) {
        if (!message) return;
        var id = message.id ? String(message.id) : '';
        var body = message.body || '';
        var createdAt = message.createdAt || '';
        var key = id || (message.senderType + '|' + body + '|' + createdAt);
        if (seen[key]) return;
        seen[key] = true;
        merged.push(message);
    });

    return merged;
};

const getLocalMessagesToPreserve = function(prevMessages, fetchedMessages) {
    return (prevMessages || []).filter(function(message) {
        if (!isLocalMessage(message)) return false;
        return !hasEquivalentBackendMessage(message, fetchedMessages || []);
    });
};

// Chat PDF export: messages per request, and a runaway cap on the cursor loop.
const CHAT_PDF_PAGE_SIZE = 50;
const CHAT_PDF_MAX_PAGES = 300;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HashtagDrilldown = ({ match, history, user }) => {
    var currentUserId = user && user.profile && user.profile._id;
    const orgPrefix = getOrgPrefix(user);
    const hashtag = (match.params.hashtag || '').toUpperCase();
    const titleHashtag = hashtag.charAt(0).toUpperCase() + hashtag.slice(1).toLowerCase();
    const displayHashtag = withHashtagPrefix(titleHashtag, orgPrefix);

    // groupId always comes from URL param — route is /:hashtag/:groupId (UserChatsPage)
    const groupId = match.params.groupId || null;

    // --- Message feed ---
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasOlder, setHasOlder] = useState(false);
    // Cursor format per API spec: "<createdAtMs>_<messageId>"
    const [nextCursor, setNextCursor] = useState(null);

    // --- Composer ---
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);

    // --- Reply context ---
    // Set when user clicks Reply on a participant message
    // Shape: { messageId, senderName, senderPhone, userId } | null
    const [replyContext, setReplyContext] = useState(null);

    // --- Error ---
    const [error, setError] = useState(null);

    // bug903: Chat/Insights sub-tab, plus the extra group-details fields
    // fetched by fetchGroupDetails below.
    const [activeSubTab, setActiveSubTab] = useState('chat');
    const [groupDescription, setGroupDescription] = useState(null);
    const [groupCreatedAt, setGroupCreatedAt] = useState(null);
    // Stored total, not the loaded array length (which is capped at one page).
    const [groupMessageCount, setGroupMessageCount] = useState(null);

    // --- Chat PDF export ---
    const [isDownloadingChat, setIsDownloadingChat] = useState(false);
    const [chatPdfMessages, setChatPdfMessages] = useState(null);

    // --- Refs ---
    const chatRef = useRef(null);
    const textareaRef = useRef(null);
    const chatPdfRef = useRef(null);
    const isMountedRef = useRef(true);

    // -----------------------------------------------------------------------
    // Hide parent scrollbar while drilldown is mounted
    // -----------------------------------------------------------------------
    useEffect(function() {
        document.body.style.overflow = 'hidden';
        return function() { document.body.style.overflow = ''; };
    }, []);

    useEffect(function() {
        return function() { isMountedRef.current = false; };
    }, []);

    // -----------------------------------------------------------------------
    // Mount: fetch latest messages
    // -----------------------------------------------------------------------
    // Also used by "Refresh chat" so groupMessageCount doesn't go stale next
    // to a message list that just did refresh.
    const fetchGroupDetails = function() {
        axios.get(url.textifyGetGroupDetails(groupId))
            .then(function(res) {
                if (res && res.data) {
                    setGroupDescription(res.data.description || null);
                    setGroupCreatedAt(res.data.createdAt || null);
                    setGroupMessageCount(typeof res.data.messageCount === 'number' ? res.data.messageCount : null);
                }
            })
            .catch(function(err) {
                console.log('Failed to load group details:', err);
            });
    };

    useEffect(() => {
        if (!groupId) {
            setLoading(false);
            setError('Group not found. Please go back and try again.');
            return;
        }
        fetchMessages();
        fetchGroupDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // -----------------------------------------------------------------------
    // Format raw API messages into the shape this component uses
    // -----------------------------------------------------------------------
    const formatMessages = (rawMessages) => {
        return rawMessages.map(function(m) {
            // userId is now a populated object { _id, firstName, lastName, mobileNumber }
            // Guard for backward compat in case it is still a plain string
            var userObj = (m.userId && typeof m.userId === 'object') ? m.userId : null;
            var userIdStr = userObj ? userObj._id : (m.userId || null);

            var firstName = (userObj && userObj.firstName) ? userObj.firstName : '';
            var lastName = (userObj && userObj.lastName) ? userObj.lastName : '';
            var fullName = (firstName + ' ' + lastName).trim();
            // Who authored the message, not whether they're currently a group recipient —
            // a non-recipient org admin's own reply must still show as ADMIN (bug908).
            const isAdminAuthored = m.source === 'platform_reply' || m.source === 'admin_sms_reply';
            var senderName = fullName
                ? fullName
                : (isAdminAuthored ? 'Recipient' : 'Not Registered');

            var senderPhone = (!isAdminAuthored && userObj) ? (userObj.mobileNumber || null) : null;

            return {
                id: m._id,
                userId: userIdStr,
                targetUserId: m.targetUserId || null,
                senderType: isAdminAuthored ? 'ADMIN' : 'PARTICIPANT',
                senderName: senderName,
                senderPhone: senderPhone,
                body: m.body,
                createdAt: m.createdAt,
                // sentiment from textSentimentsData (present on inbound participant messages)
                sentiment: (m.textSentimentsData && m.textSentimentsData.sentiment) || null,
                source: m.source || null,
                replyToId: m.replyToId || null,
                replyToBodyPreview: m.replyToBodyPreview || null,
                attachment: getFirstMessagingAttachment(m),
            };
        });
    };

    // -----------------------------------------------------------------------
    // Fetch messages — GET /textify-group-chat/:groupId/messages
    // cursor = null: initial load (last 20 messages)
    // cursor = "<createdAtMs>_<messageId>": load older 20 messages
    // -----------------------------------------------------------------------
    const fetchMessages = (cursor) => {
        if (!cursor) {
            setError(null);
            setLoading(true);
        } else {
            setLoadingOlder(true);
        }

        var params = { limit: 20 };
        if (cursor) params.before = cursor;

        axios.get(url.textifyGetGroupMessages(groupId), { params: params })
            .then(function(res) {
                var rawMessages = (res.data && res.data.messages) || [];
                var resNextCursor = (res.data && res.data.nextCursor) || null;
                var resHasMore = (res.data && res.data.hasMore) || false;

                var formatted = formatMessages(rawMessages);

                if (!cursor) {
                    // Initial load / refresh — keep any local optimistic replies that
                    // have not come back from the backend yet instead of wiping them.
                    var fetchedMessages = applyPhoneMap(sortMessagesAsc(formatted));
                    setMessages(function(prev) {
                        if (fetchedMessages.length === 0 && prev && prev.length > 0) {
                            return prev;
                        }
                        var localMessages = getLocalMessagesToPreserve(prev, fetchedMessages);
                        return applyPhoneMap(sortMessagesAsc(mergeChatMessages(fetchedMessages.concat(localMessages))));
                    });
                    setHasOlder(resHasMore);
                    setNextCursor(resNextCursor);
                    setTimeout(function() {
                        if (chatRef.current) {
                            chatRef.current.scrollTop = chatRef.current.scrollHeight;
                        }
                    }, 0);
                } else {
                    // Load older — prepend older messages before existing, then sort to keep order correct
                    var container = chatRef.current;
                    var prevScrollHeight = (container && container.scrollHeight) || 0;

                    setMessages(function(prev) {
                        var combined = sortMessagesAsc(mergeChatMessages(formatted.concat(prev)));
                        return applyPhoneMap(combined);
                    });
                    setHasOlder(resHasMore);
                    setNextCursor(resNextCursor);

                    setTimeout(function() {
                        if (container) {
                            container.scrollTop = container.scrollHeight - prevScrollHeight;
                        }
                    }, 0);
                }
            })
            .catch(function(err) {
                console.error('[HashtagDrilldown] Error fetching messages:', err);
                setError('Failed to load messages. Please try again.');
            })
            .finally(function() {
                setLoading(false);
                setLoadingOlder(false);
            });
    };

    // Whole-thread PDF: pages the API into a local list, never touching on-screen state.
    const handleDownloadChat = async () => {
        if (isDownloadingChat || !groupId) return;
        setIsDownloadingChat(true);
        let chunked = null;
        try {
            let fetched = [];
            let cursor = null;
            let hasMore = true;
            let pageCount = 0;
            const seenCursors = new Set();

            while (hasMore) {
                pageCount += 1;
                if (pageCount > CHAT_PDF_MAX_PAGES) throw new Error('Chat has too many pages to export');

                const params = { limit: CHAT_PDF_PAGE_SIZE };
                if (cursor) params.before = cursor;
                const res = await axios.get(url.textifyGetGroupMessages(groupId), { params: params });
                if (!isMountedRef.current) return;

                const data = res.data || {};
                const rawMessages = data.messages || [];
                fetched = fetched.concat(formatMessages(rawMessages));
                hasMore = !!data.hasMore;

                if (hasMore) {
                    const nextCursor = data.nextCursor || null;
                    if (rawMessages.length === 0 || !nextCursor || seenCursors.has(nextCursor)) {
                        throw new Error('Chat pagination did not advance');
                    }
                    seenCursors.add(nextCursor);
                    cursor = nextCursor;
                }
            }

            setChatPdfMessages(applyPhoneMap(sortMessagesAsc(mergeChatMessages(fetched))));
            await waitForNextFrame();
            if (!isMountedRef.current) return;

            const docNode = chatPdfRef.current;
            if (!docNode) throw new Error('Chat PDF node is not mounted');
            chunked = splitIntoPdfChunks(docNode);
            await exportNodesToPdf(chunked.nodes, buildPdfFilename(displayHashtag, HASHTAG_PDF_CHAT_LABEL));
        } catch (err) {
            console.error('[HashtagDrilldown] Error exporting chat PDF:', err);
            toastContainer(HASHTAG_CHAT_PDF_FAILED, 'error');
        } finally {
            if (chunked) chunked.dispose();
            if (isMountedRef.current) {
                setChatPdfMessages(null);
                setIsDownloadingChat(false);
            }
        }
    };

    // -----------------------------------------------------------------------
    // Scroll handler — trigger load older when scrolling near top
    // -----------------------------------------------------------------------
    const handleScroll = () => {
        if (!chatRef.current) return;
        if (chatRef.current.scrollTop < 50 && hasOlder && !loadingOlder && !loading) {
            fetchMessages(nextCursor);
        }
    };

    // -----------------------------------------------------------------------
    // Reply button — pre-fill composer and set replyContext
    // -----------------------------------------------------------------------
    const handleReply = (msg) => {
        setReplyContext({
            messageId: msg.id,
            senderName: msg.senderName,
            senderPhone: msg.senderPhone,
            userId: msg.userId,
            quotedBody: msg.body,
        });
        // Pre-fill with @Name if available, otherwise @+phone
        var nameIsReal = msg.senderName && msg.senderName !== 'Not Registered' && msg.senderName !== 'Participant';
        var replyTag = nameIsReal ? ('@' + msg.senderName + ' ') : (msg.senderPhone ? ('@' + msg.senderPhone + ' ') : '@');
        setReplyText(replyTag);

        setTimeout(function() {
            if (textareaRef.current) {
                textareaRef.current.focus();
                var len = textareaRef.current.value.length;
                textareaRef.current.setSelectionRange(len, len);
            }
        }, 0);
    };

    const dismissReplyContext = () => {
        setReplyContext(null);
    };

    // -----------------------------------------------------------------------
    // Send — POST /textify-group-chat/reply
    // Payload: { groupId, body, targetMobileNumber }
    // -----------------------------------------------------------------------
    const handleSend = () => {
        var trimmed = (replyText || '').trim();
        if (!trimmed || sending || !groupId) return;

        // Resolve targetPhoneNumber:
        // (1) @+phone in body text → (2) replyContext phone → (3) last participant phone
        var targetPhoneNumber = resolveTargetMobile(trimmed, messages, replyContext);
        if (!targetPhoneNumber) {
            toastContainer('No recipient found. Click Reply on a message or use @+phone in your text.', 'error');
            return;
        }

        setSending(true);
        setError(null);

        // Strip @+phone from body — participant knows the text is for them.
        // targetMobileNumber is sent as a separate field for routing.
        var cleanBody = trimmed.replace(/@\+\d{7,15}\s*/, '').trim();

        // Build optional replyTo fields from replyContext
        var replyToId = (replyContext && replyContext.messageId) || undefined;
        var replyToBodyPreview = undefined;
        if (replyContext && replyContext.quotedBody) {
            // Strip the hashtag group name (e.g. "#dyquest-safety ") from the preview
            var previewText = replyContext.quotedBody.replace(/#\S+\s*/, '').trim();
            replyToBodyPreview = previewText.length > 100 ? previewText.slice(0, 100).trim() : previewText;
        }

        // Resolve targetUserId: from replyContext or by matching phone to a participant in messages
        var targetUserId = (replyContext && replyContext.userId) || null;
        if (!targetUserId && targetPhoneNumber) {
            for (var i = 0; i < messages.length; i++) {
                if (messages[i].senderPhone && messages[i].senderPhone === targetPhoneNumber && messages[i].userId) {
                    targetUserId = messages[i].userId;
                    break;
                }
            }
        }
        var payload = {
            groupId: groupId,
            hashtagName: hashtag.toLowerCase(),
            body: cleanBody,
            targetMobileNumber: targetPhoneNumber,
            targetUserId: targetUserId,
            mode: 'direct',
        };
        if (replyToId) { payload.replyToId = replyToId; }
        if (replyToBodyPreview) { payload.replyToBodyPreview = replyToBodyPreview; }

        // Optimistic UI — append pending message immediately
        var tempId = 'local-' + Date.now();
        var newMsg = {
            id: tempId,
            userId: currentUserId || null,
            targetUserId: (replyContext && replyContext.userId) || null,
            senderType: 'ADMIN',
            senderName: 'You',
            senderPhone: null,
            body: cleanBody,
            createdAt: new Date().toISOString(),
            sentiment: null,
            source: 'platform_reply',
            pending: true,
            failed: false,
            attachment: null,
            replyToId: replyToId || null,
            replyToBodyPreview: replyToBodyPreview || null,
        };

        setMessages(function(prev) { return prev.concat(newMsg); });
        setReplyText('');
        setReplyContext(null);

        // Scroll to bottom after optimistic insert
        setTimeout(function() {
            if (chatRef.current) {
                chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
        }, 0);

        axios.post(url.textifySendMessage, payload)
            .then(function(res) {
                var data = (res && res.data) || {};
                var savedMessage = extractSavedMessageFromSendResponse(data);
                var mappedSavedMessage = savedMessage ? formatMessages([savedMessage])[0] : null;
                var realId = data.messageId || data.id || (mappedSavedMessage && mappedSavedMessage.id) || tempId;

                // Keep the message visible immediately. If the backend returns a
                // saved message object, replace the temporary object. If it only
                // returns { ok: true } / { messageId }, just clear the pending state.
                setMessages(function(prev) {
                    return (prev || []).map(function(m) {
                        if (m.id !== tempId) return m;
                        if (mappedSavedMessage) {
                            return Object.assign({}, mappedSavedMessage, {
                                id: realId,
                                pending: false,
                                failed: false,
                            });
                        }
                        return Object.assign({}, m, {
                            id: realId,
                            pending: false,
                            failed: false,
                        });
                    });
                });
                toastContainer('Message sent.', 'success');
            })
            .catch(function(err) {
                console.error('[HashtagDrilldown] Error sending message:', err);

                // Do not remove the optimistic message. Mark it failed so the
                // admin can see what they tried to send instead of thinking the
                // UI lost their reply.
                setMessages(function(prev) {
                    return (prev || []).map(function(m) {
                        if (m.id !== tempId) return m;
                        return Object.assign({}, m, { pending: false, failed: true });
                    });
                });

                var errData = err.response && err.response.data;
                var beMsg = errData && (errData.message || errData.error);
                var errMsg = (beMsg && beMsg.includes('you entered is not in')) ? beMsg : 'Failed to send message. Please try again.';
                toastContainer(errMsg, 'error');
            })
            .finally(function() {
                setSending(false);
            });
    };

    // -----------------------------------------------------------------------
    // Keyboard: Cmd/Ctrl + Enter to send
    // -----------------------------------------------------------------------
    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    // -----------------------------------------------------------------------
    // Name maps for admin message display: show @Name instead of @+phone
    // -----------------------------------------------------------------------
    var phoneNameMap = {};
    var userIdNameMap = {};
    messages.forEach(function(m) {
        if (m.senderType === 'PARTICIPANT') {
            if (m.senderPhone && m.senderName && m.senderName !== 'Participant') {
                phoneNameMap[m.senderPhone] = m.senderName;
            }
            if (m.userId && m.senderName && m.senderName !== 'Participant') {
                userIdNameMap[m.userId] = m.senderName;
            }
        }
    });

    // Fallback for unregistered participants with no name
    var displayName = function(name) {
        if (!name || name.trim() === '' || name.trim().toLowerCase() === 'undefined undefined' || name.trim().toLowerCase() === 'undefined') {
            return 'Not Registered';
        }
        return name;
    };

    // Strip @+phone from body for display (old messages have it, new ones don't)
    var cleanAdminBody = function(body) {
        return body.replace(/@\+\d{7,15}\s*/, '').trim();
    };

    // Most recent message from targetUserId before msgIndex, in `list` (default: live messages)
    const findQuotedMessage = function(targetUserId, msgIndex, list) {
        const source = list || messages;
        if (!targetUserId) return null;
        for (let i = msgIndex - 1; i >= 0; i--) {
            if (source[i].userId === targetUserId) {
                return source[i];
            }
        }
        return null;
    };

    // One PDF transcript block: the on-screen bubble minus avatars, Reply and pending markers.
    const renderChatPdfMessage = function(msg, msgIndex, list) {
        const isAdmin = msg.senderType === 'ADMIN';
        const quotedMsg = isAdmin ? findQuotedMessage(msg.targetUserId, msgIndex, list) : null;
        const quotePreview = msg.replyToBodyPreview || (quotedMsg ? quotedMsg.body : null);
        const quoteName = quotedMsg ? displayName(quotedMsg.senderName) : null;
        const displayBody = isAdmin ? cleanAdminBody(msg.body || '') : msg.body;
        const pdfAttachment = msg.attachment ? normalizeAttachment(msg.attachment) : null;
        const timestamp = formatPdfTimestamp(msg.createdAt);
        const showSentiment = msg.sentiment && !(msg.attachment && msg.attachment.kind === 'audio');
        return (
            <div
                key={msg.id || ('pdf-' + msgIndex)}
                className={'uc-pdf-msg ' + (isAdmin ? 'uc-pdf-msg-admin' : 'uc-pdf-msg-participant')}
            >
                <div className="uc-pdf-meta">
                    <span className="uc-pdf-sender">{displayName(msg.senderName)}</span>
                    {timestamp ? <span>{timestamp}</span> : null}
                    {msg.senderPhone ? <span className="uc-pdf-phone">{msg.senderPhone}</span> : null}
                    {showSentiment ? (
                        <span className={'uc-pill sentiment-' + msg.sentiment.toLowerCase() + ' mini'}>
                            <span className="dot">●</span>
                            {msg.sentiment.charAt(0) + msg.sentiment.slice(1).toLowerCase()}
                        </span>
                    ) : null}
                </div>
                <div className="uc-pdf-bubble">
                    {quotePreview ? (
                        <div className="uc-pdf-quote">
                            {quoteName ? <span className="uc-pdf-quote-name">{quoteName}</span> : null}
                            <span className="uc-pdf-quote-body">{quotePreview}</span>
                            {quotedMsg && quotedMsg.attachment ? (
                                <span className="uc-pdf-quote-body">{HASHTAG_PDF_ATTACHMENT_LABEL}</span>
                            ) : null}
                        </div>
                    ) : null}
                    {displayBody ? <div className="uc-pdf-text">{displayBody}</div> : null}
                    {pdfAttachment ? (
                        <div className="uc-pdf-attachment">{buildHashtagPdfAttachmentLine(pdfAttachment.fileName)}</div>
                    ) : null}
                </div>
            </div>
        );
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className="uc-drilldown-page">

            {/* Header — single row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span onClick={function() { var overviewPath = match.url.replace(/\/[^/]+\/[^/]+$/, ''); history.push(overviewPath); }} className="cursor-pointer back-link" style={{ display: 'flex', alignItems: 'center', color: '#64748b', fontSize: '14px' }}>
                    <BiArrowBack className="me-1" /> User Chats
                </span>
                <span style={{ color: '#64748b', fontSize: '12px' }}>&gt;</span>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>#{displayHashtag}</h2>
                <span
                    onClick={function() { setNextCursor(null); fetchMessages(); fetchGroupDetails(); }}
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#64748b' }}
                >
                    <BiRefresh size={16} /> Refresh chat
                </span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                    {loading ? 'Loading...' : ((typeof groupMessageCount === 'number' ? groupMessageCount : messages.filter(function(m) { return !m.pending; }).length) + ' messages')}
                </span>
                {activeSubTab === 'chat' ? (
                    <HashtagDownloadButton
                        onClick={handleDownloadChat}
                        disabled={loading || isDownloadingChat || messages.length === 0}
                        isBusy={isDownloadingChat}
                    />
                ) : null}
            </div>
            {/* bug903: Chat/Insights sub-tab, same visual pattern as the top-level User Chats/Insights tabs */}
            <div className="uc-tabs-style uc-drilldown-subtabs">
                <span
                    className={'uc-tab-item' + (activeSubTab === 'chat' ? ' active' : '')}
                    onClick={() => setActiveSubTab('chat')}
                >
                    Chat
                </span>
                <span
                    className={'uc-tab-item' + (activeSubTab === 'insights' ? ' active' : '')}
                    onClick={() => setActiveSubTab('insights')}
                >
                    Insights
                </span>
            </div>

            {activeSubTab === 'insights' ? (
                <div className="uc-insights-cards-scroll uc-drilldown-insights">
                    <HashtagInsightsCard
                        groupId={groupId}
                        hashtagLabel={displayHashtag}
                        description={groupDescription}
                        createdAt={groupCreatedAt}
                        embedded
                        showDownload
                    />
                </div>
            ) : (
            <React.Fragment>
            {/* Flat feed container */}
            <div className="uc-flat-feed-container">

                {/* Message thread */}
                <div
                    className="uc-message-thread"
                    ref={chatRef}
                    onScroll={handleScroll}
                >
                    {loadingOlder && (
                        <div className="loading-older">Loading older messages...</div>
                    )}

                    {!hasOlder && !loading && messages.length > 0 && (
                        <div className="beginning-of-conversation">
                            <BiCheckCircle className="me-1" /> Beginning of conversation
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-older" style={{ padding: '40px 0' }}>
                            Loading messages...
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="loading-older" style={{ padding: '40px 0' }}>
                            No messages yet in #{titleHashtag}
                        </div>
                    ) : (
                        messages.map(function(msg, msgIndex) {
                            const isMe = msg.senderType === 'ADMIN';
                            var quotedMsg = msg.senderType === 'ADMIN' ? findQuotedMessage(msg.targetUserId, msgIndex) : null;
                            var quotePreview = msg.replyToBodyPreview || (quotedMsg ? quotedMsg.body : null);
                            var quoteAttachment = quotedMsg ? quotedMsg.attachment : null;
                            var quoteName = quotedMsg ? displayName(quotedMsg.senderName) : (msg.targetUserId && userIdNameMap[msg.targetUserId] ? userIdNameMap[msg.targetUserId] : null);
                            var displayBody = msg.senderType === 'ADMIN' ? cleanAdminBody(msg.body) : msg.body;
                            return (
                                <div
                                    key={msg.id}
                                    className={'uc-msg ' + (isMe ? 'admin-msg' : 'user-msg')}
                                >
                                    {/* Message meta */}
                                    <div className="msg-meta">
                                        {!isMe && (
                                            <div
                                                className="uc-avatar mini"
                                                style={{ backgroundColor: '#e2e8f0', color: '#475569' }}
                                            >
                                                {getInitials(displayName(msg.senderName))}
                                            </div>
                                        )}
                                        <span className="uc-fw-500-dark">{displayName(msg.senderName)}</span>
                                        <span>{formatMessageTimestamp(msg.createdAt)}</span>
                                        {msg.senderPhone && (
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                {msg.senderPhone}
                                            </span>
                                        )}
                                        {/* Hide sentiment on audio inputs: it's scored on the accompanying text, not the audio, so it's not meaningful */}
                                        {msg.sentiment && !(msg.attachment && msg.attachment.kind === 'audio') && (
                                            <span className={'uc-pill sentiment-' + msg.sentiment.toLowerCase() + ' mini'}>
                                                <span className="dot" style={{ fontSize: '8px' }}>●</span>
                                                {msg.sentiment.charAt(0) + msg.sentiment.slice(1).toLowerCase()}
                                            </span>
                                        )}
                                        {isMe && (
                                            <div className="uc-avatar mini">
                                                {getInitials(displayName(msg.senderName))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Message bubble */}
                                    <div className="msg-bubble">
                                        {quotePreview && (
                                            <div className="msg-quote">
                                                {quoteName && <span className="msg-quote-name">{quoteName}</span>}
                                                {quotePreview ? <span className="msg-quote-body">{quotePreview}</span> : null}
                                                {quoteAttachment ? <span className="msg-quote-body">📎 Attachment</span> : null}
                                            </div>
                                        )}
                                        {displayBody ? <div className="msg-text">{displayBody}</div> : null}
                                        {msg.attachment ? (
                                            <div className="msg-attachment-stack">
                                                {renderMessageAttachment(msg.attachment, isMe)}
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Reply button — participant messages only */}
                                    {msg.senderType === 'PARTICIPANT' && !msg.pending && (
                                        <button
                                            className="uc-reply-btn"
                                            onClick={() => handleReply(msg)}
                                        >
                                            <BsReply style={{ marginRight: '4px' }} /> Reply
                                        </button>
                                    )}

                                    {/* Pending indicator */}
                                    {msg.pending && (
                                        <span className="uc-msg-pending">Sending...</span>
                                    )}
                                    {msg.failed && (
                                        <span className="uc-msg-pending failed">Failed to send</span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Composer */}
                <div className="uc-reply-composer">

                    {/* Reply context banner */}
                    {replyContext && (
                        <div className="uc-reply-context-banner">
                            <div className="uc-reply-context-info">
                                <span className="uc-reply-context-label">Replying to</span>
                                <span className="uc-reply-context-name">{displayName(replyContext.senderName)}</span>
                                {replyContext.senderPhone && (
                                    <span className="uc-reply-context-phone">{replyContext.senderPhone}</span>
                                )}
                            </div>
                            <button className="uc-reply-context-dismiss" onClick={dismissReplyContext}>
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Error banner */}
                    {error && (
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            borderRadius: '6px',
                            fontSize: '12px',
                            marginBottom: '8px',
                        }}>
                            {error}
                        </div>
                    )}

                    <textarea
                        ref={textareaRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message... Use @+phone to target a specific participant"
                        disabled={sending}
                    />

                    <div className="uc-composer-footer">
                        <span>Cmd+Enter to send &middot; #{titleHashtag.toLowerCase()}</span>
                        <button
                            className={'uc-send-btn ' + (!replyText.trim() || sending ? 'disabled' : '')}
                            disabled={!replyText.trim() || sending}
                            onClick={handleSend}
                        >
                            <BsSend /> {sending ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                </div>
            </div>
            </React.Fragment>
            )}

            {chatPdfMessages ? (
                <HashtagPdfDocument
                    docRef={chatPdfRef}
                    hashtagLabel={displayHashtag}
                    subtitle={HASHTAG_PDF_CHAT_LABEL}
                    extraLine={buildHashtagPdfMessageCount(chatPdfMessages.length)}
                >
                    {chatPdfMessages.map(function(msg, msgIndex) {
                        return renderChatPdfMessage(msg, msgIndex, chatPdfMessages);
                    })}
                </HashtagPdfDocument>
            ) : null}
        </div>
    );
};

export default HashtagDrilldown;
